<template>
  <v-form @submit.prevent="submitArticle">
    <CreateArticleInfoDialog
      ref="createArticleInfoDialog"
    ></CreateArticleInfoDialog>
    <UpdateArticleInfoDialog
      ref="updateArticleInfoDialog"
    ></UpdateArticleInfoDialog>
    <!--Dialog that comes up when creating or updating a post-->
    <v-dialog v-model="isUploading" fullscreen>
      <v-container fluid fill-height style="background-color: rgba(0,0,0,0.5)">
        <v-row>
          <v-col>
            <v-card class="elevation-12">
              <v-toolbar center dark color="primary">
                <v-spacer></v-spacer>
                <v-toolbar-title>
                  {{
                    $t("createArticle.general.Saving in progress, please wait")
                  }}
                  <v-progress-circular indeterminate class="ml-2" />
                </v-toolbar-title>
                <v-spacer></v-spacer>
              </v-toolbar>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </v-dialog>
    <v-row v-if="$vuetify.breakpoint.smAndUp" class="ma-0 pa-0">
      <!--Title-->
      <v-col md="12" class="ma-0 pa-0">
        <div class="pb-5 d-flex">
          <span
            class="display-1 pt-1 font-weight-bold"
            v-if="isArticleUpdate"
            >{{ $t("createArticle.general.Create article") }}</span
          >
          <span class="display-1 pt-1 font-weight-bold" v-else>{{
            $t("createArticle.general.Update article")
          }}</span>
          <span v-if="isArticleUpdate">
            <v-btn
              :title="$t('createArticle.general.Click to see more information')"
              @click.stop="$refs.createArticleInfoDialog.openDialog()"
              icon
              large
            >
              <v-icon color="primary">far fa-question-circle</v-icon>
            </v-btn>
          </span>
          <span v-else>
            <v-btn
              :title="$t('createArticle.general.Click to see more information')"
              @click.stop="$refs.updateArticleInfoDialog.openDialog()"
              icon
              large
            >
              <v-icon color="primary">far fa-question-circle</v-icon>
            </v-btn>
          </span>
        </div>
      </v-col>
      <!--Display languages the article are created in-->
      <v-col md="12" class="ma-0 pa-0">
        <v-row>
          <v-spacer></v-spacer>
          <v-col
            v-for="item in Object.keys(ArticleData)"
            :key="item"
            md="auto"
            class="ma-0 pa-0"
          >
            <span
              class="flag-icon flag-icon-no"
              v-if="item == 'nb' && ArticleData[item].Title"
            ></span>
            <span
              class="flag-icon flag-icon-gb"
              v-if="item == 'en' && ArticleData[item].Title"
            ></span>
            <span
              class="flag-icon flag-icon-es"
              v-if="item == 'es' && ArticleData[item].Title"
            ></span>
          </v-col>
          <v-spacer></v-spacer>
        </v-row>
      </v-col>
      <!--Publish?-->
      <v-col md="6">
        <v-switch v-model="Published" :label="`${PublishText}`"></v-switch>
      </v-col>
      <!--Enable comments?-->
      <v-col md="6">
        <v-switch
          v-model="Comments"
          :label="
            Comments
              ? $t('createArticle.settings.Comments is enabled')
              : $t('createArticle.settings.Comments is disabled')
          "
        ></v-switch>
      </v-col>
      <!--Main category selection-->
      <!-- <v-col md="12">
        <v-select
          :items="MainTags"
          box
          :label="$t('createArticle.form.Choose category')"
          v-model="Category"
          item-text="Label"
          item-value="id"
          return-object
        ></v-select>
      </v-col> -->
      <!--Geo Tag-->
      <v-col md="6">
        <v-autocomplete
          :label="$t('createArticle.form.GeoTagging')"
          v-model="GeoTags"
          :items="Geo.SearchTags"
          :loading="Geo.IsLoading"
          :search-input.sync="Geo.Search"
          :placeholder="
            $t('createArticle.general.Write here to find location tags')
          "
          item-text="Label"
          item-value="id"
          multiple
          chips
          return-object
          @change="selectedGeoEvent"
        >
          <template v-slot:selection="data">
            <v-chip
              :selected="data.selected"
              close
              class="chip--select-multi"
              @click:close="removeGeoTag(data.item)"
              @input="removeGeoTag(data.item)"
              >#{{ data.item.Label }}</v-chip
            >
          </template>
          <template v-slot:item="data">
            <div class="mb-3">
              <div class="title">#{{ data.item.Label }}</div>
              <div class="body-1" v-text="data.item.Path"></div>
              <div class="caption" v-text="data.item.Category"></div>
            </div>
          </template>
        </v-autocomplete>
      </v-col>
      <!--Tagging-->
      <v-col md="6">
        <v-autocomplete
          :label="$t('createArticle.form.Tagging')"
          v-model="Tags"
          :items="TagsOrdered"
          :loading="Tag.IsLoading"
          :search-input.sync="Tag.Search"
          :placeholder="
            $t('createArticle.general.Write here to find interest tags')
          "
          item-text="Label"
          item-value="id"
          autocomplete
          multiple
          chips
          return-object
          @change="selectedTagEvent"
        >
          <template v-slot:selection="data">
            <v-chip
              :selected="data.selected"
              close
              class="chip--select-multi"
              @click:close="removeTag(data.item)"
              @input="removeTag(data.item)"
              >#{{ data.item.Label }}
            </v-chip>
          </template>
          <template v-slot:item="data">
            <v-row>
              <v-col xs12 sm12>
                <div class="headline">#{{ data.item.Label }}</div>
              </v-col>
            </v-row>
          </template>
          <!--Custom code if there is no tags found-->
          <template slot="no-data">
            <v-row row wrap>
              <v-col class="text-center">
                <span
                  >{{
                    $t("createArticle.form.Could not find the searched word")
                  }}
                  :
                </span>
                <span class="font-weight-bold">{{ Tag.Search }}</span>
              </v-col>
            </v-row>
          </template>
        </v-autocomplete>
      </v-col>
      <!--Image-->
      <v-col xs="12" sm="6">
        <div>
          <div class="imageuploadwrapper" @click="$refs.fileupload.click()">
            <v-img
              v-if="MainImage.Exists"
              :src="MainImage.Url"
              contain
              max-height="100%"
              max-width="100%"
            >
              <v-toolbar dense flat right color="transparent">
                <v-spacer></v-spacer>
                <v-btn
                  class="red darken-1 white--text"
                  @click.stop="setImgDirty"
                  >{{ $t("createArticle.form.Remove") }}</v-btn
                >
              </v-toolbar>
            </v-img>
            <span
              v-else
              style="display: block; width: 100%; font-size: 2rem;"
              >{{ $t("createArticle.form.Select an image") }}</span
            >
          </div>
          <div style="display: none">
            <input
              type="file"
              name="file"
              ref="fileupload"
              id="fileupload"
              @change="handleFileSelect"
            />
          </div>
        </div>
      </v-col>
      <v-col xs12 sm6>
        <v-select
          box
          :items="getLanguages"
          item-text="name"
          item-value="code"
          :label="$t('createArticle.form.Language')"
          v-model="Lang"
        ></v-select>
        <v-text-field
          v-model="Title"
          :placeholder="$t('createArticle.form.Add headline')"
          :label="$t('createArticle.form.Headline')"
          color="green"
          class="headline"
          height="56"
          :rules="[rules.max250Chars]"
          maxlength="250"
          counter="250"
        ></v-text-field>
        <v-textarea
          :label="$t('createArticle.form.Content')"
          v-model="Content"
          @paste="pasteEventRemoveHtml"
          name="name"
          auto-grow
        ></v-textarea>
        <v-row v-if="this.Id">
          <v-col cols="12" class="py-0 pb-2">
            <v-btn
              class="pl-3"
              block
              color="success"
              type="submit"
              :disabled="isUploading"
              >{{ $t("createArticle.general.Update article") }}</v-btn
            >
          </v-col>
          <v-col cols="12" class="py-0 pb-6">
            <v-btn
              block
              color="warning"
              type="button"
              :disabled="isUploading"
              @click="deleteArticle"
              >{{ $t("createArticle.general.Delete article") }}</v-btn
            >
          </v-col>
        </v-row>
        <!--No article - new one to create-->
        <v-row v-else>
          <!--Publish article Button -->
          <v-col cols="12" class="ma-0 pa-0" v-if="Published == true">
            <v-btn
              class=" ml-12"
              block
              color="success"
              type="submit"
              :disabled="isUploading"
              >{{ $t("createArticle.general.Publish article") }}</v-btn
            >
          </v-col>
          <!--Dont Publish - but save article-->
          <v-col cols="12" class="ma-0 pa-0" v-else>
            <v-btn
              class=" ml-12"
              block
              color="success"
              type="submit"
              :disabled="isUploading"
              >{{ $t("createArticle.general.Create article") }}</v-btn
            >
          </v-col>
        </v-row>
      </v-col>
    </v-row>

    <!--Phone-->
    <v-row v-else class="ma-0 pa-0">
      <!--Title and icon-->
      <v-col cols="12" class="ma-0 pa-0" v-if="this.Id">
        <v-row class="ma-0 pa-0">
          <v-col cols="8" class="py-0 pl-6 pt-3">
            <p class="headline text-center pt-1 font-weight-bold">
              {{ $t("createArticle.general.Update article") }}
            </p>
          </v-col>
          <v-col cols="auto" class="py-0 pt-3">
            <v-btn
              :title="$t('createArticle.general.Click to see more information')"
              @click.stop="$refs.updateArticleInfoDialog.openDialog()"
              icon
              large
            >
              <v-icon color="primary">far fa-question-circle</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
      <v-col v-else cols="12" class="ma-0 pa-0">
        <v-row class="ma-0 pa-0">
          <v-col cols="8" class="py-0 pl-12 pt-3">
            <p class="headline text-center pt-1 font-weight-bold">
              {{ $t("createArticle.general.Create article") }}
            </p>
          </v-col>
          <v-col cols="auto" class="py-0 pl-0 pt-2">
            <v-btn
              :title="$t('createArticle.general.Click to see more information')"
              @click.stop="$refs.createArticleInfoDialog.openDialog()"
              icon
              large
            >
              <v-icon color="primary">far fa-question-circle</v-icon>
            </v-btn>
          </v-col>
        </v-row>
      </v-col>
      <!--Publish article?-->
      <v-col cols="12" class="ma-0 pa-0">
        <v-switch
          class="pl-3"
          v-model="Published"
          :label="`${PublishText}`"
        ></v-switch>
      </v-col>
      <!--Comments enabled?-->
      <v-col cols="12" class="ma-0 pa-0">
        <v-switch
          class="pl-3"
          v-model="Comments"
          :label="
            Comments
              ? $t('createArticle.settings.Comments is enabled')
              : $t('createArticle.settings.Comments is disabled')
          "
        ></v-switch>
      </v-col>
      <!--Location tags-->
      <v-col cols="12" class="ma-0 pa-0 pl-5 pt-3">
        <v-autocomplete
          :label="$t('createArticle.form.GeoTagging')"
          :placeholder="
            $t('createArticle.general.Write here to find location tags')
          "
          v-model="GeoTags"
          :items="Geo.SearchTags"
          :loading="Geo.IsLoading"
          :search-input.sync="Geo.Search"
          item-text="Label"
          item-value="id"
          multiple
          chips
          return-object
          @change="selectedGeoEvent"
        >
          <template v-slot:selection="data">
            <v-chip
              :selected="data.selected"
              close
              class="chip--select-multi"
              @click:close="removeGeoTag(data.item)"
              @input="removeGeoTag(data.item)"
              >#{{ data.item.Label }}</v-chip
            >
          </template>
          <template v-slot:item="data">
            <div class="mb-3">
              <div class="title">#{{ data.item.Label }}</div>
              <div class="body-1" v-text="data.item.Path"></div>
              <div class="caption" v-text="data.item.Category"></div>
            </div>
          </template>
        </v-autocomplete>
      </v-col>
      <!--Interest tags-->
      <v-col cols="12" class="ma-0 pa-0 pl-5 pt-3">
        <v-autocomplete
          :label="$t('createArticle.form.Tagging')"
          :placeholder="
            $t('createArticle.general.Write here to find interest tags')
          "
          v-model="Tags"
          :items="TagsOrdered"
          :loading="Tag.IsLoading"
          :search-input.sync="Tag.Search"
          item-text="Label"
          item-value="id"
          autocomplete
          multiple
          chips
          return-object
          @change="selectedTagEvent"
        >
          <template v-slot:selection="data">
            <v-chip
              :selected="data.selected"
              close
              class="chip--select-multi"
              @click:close="removeTag(data.item)"
              @input="removeTag(data.item)"
              >#{{ data.item.Label }}</v-chip
            >
          </template>
          <template v-slot:item="data">
            <v-row>
              <v-col xs12 sm12>
                <div class="headline">#{{ data.item.Label }}</div>
              </v-col>
            </v-row>
          </template>
          <!--Custom code if there is no tags found-->
          <template slot="no-data">
            <v-row row wrap>
              <v-col class="text-center">
                <span
                  >{{
                    $t("createArticle.form.Could not find the searched word")
                  }}
                  :
                </span>
                <span class="font-weight-bold">{{ Tag.Search }}</span>
              </v-col>
            </v-row>
          </template>
        </v-autocomplete>
      </v-col>
      <!--img-->
      <v-col cols="12" class="ma-0 pa-0 pl-5 pt-3 pb-3" style="z-index:1;">
        <div>
          <div
            class="imageuploadwrapper"
            @click="$refs.fileupload.click()"
            style="height: 200px;"
          >
            <v-img
              v-if="MainImage.Exists"
              :src="MainImage.Url"
              contain
              max-height="100%"
              max-width="100%"
            >
              <v-toolbar dense flat right color="transparent">
                <v-spacer></v-spacer>
                <v-btn
                  class="red darken-1 white--text"
                  @click.stop="setImgDirty"
                  >{{ $t("createArticle.form.Remove") }}</v-btn
                >
              </v-toolbar>
            </v-img>
            <span
              v-else
              style="display: block; width: 100%; font-size: 2rem;"
              >{{ $t("createArticle.form.Select an image") }}</span
            >
          </div>
          <div style="display: none">
            <input
              type="file"
              name="file"
              ref="fileupload"
              id="fileupload"
              @change="handleFileSelect"
            />
          </div>
        </div>
      </v-col>
      <!--Language of article-->
      <v-col cols="12" class="ma-0 pa-0 pl-6">
        <v-select
          box
          :items="getLanguages"
          item-text="name"
          item-value="code"
          :label="$t('createArticle.form.Language')"
          v-model="Lang"
        ></v-select>
      </v-col>
      <!--Headline-->
      <v-col cols="12" class="ma-0 pa-0 pl-6">
        <v-text-field
          v-model="Title"
          :placeholder="$t('createArticle.form.Add headline')"
          :label="$t('createArticle.form.Headline')"
          color="green"
          class="headline"
          height="56"
        ></v-text-field>
      </v-col>
      <!--Content-->
      <v-col cols="12" class="ma-0 pa-0 pl-6">
        <v-textarea
          :label="$t('createArticle.form.Content')"
          v-model="Content"
          @paste="pasteEventRemoveHtml"
          name="name"
          auto-grow
        ></v-textarea>
      </v-col>
      <!--If it got an ID, its an excisting article-->
      <v-row v-if="this.Id">
        <v-col cols="12" class="py-0 pb-2">
          <v-btn
            class="pl-3"
            block
            color="success"
            type="submit"
            :disabled="isUploading"
          >
            {{ $t("createArticle.general.Update article") }}</v-btn
          >
        </v-col>
        <v-col cols="12" class="py-0 pb-6">
          <v-btn
            block
            color="warning"
            type="button"
            :disabled="isUploading"
            @click="deleteArticle"
            >{{ $t("createArticle.general.Delete article") }}</v-btn
          >
        </v-col>
      </v-row>
      <!--No article - new one to create-->
      <v-row v-else>
        <!--Publish article Button -->
        <v-col cols="12" class="ma-0 pa-0" v-if="Published == true">
          <v-btn
            class=" ml-12"
            block
            color="success"
            type="submit"
            :disabled="isUploading"
            >Publiser artikkelen</v-btn
          >
        </v-col>
        <!--Dont Publish - but save article-->
        <v-col cols="12" class="ma-0 pa-0" v-else>
          <v-btn
            class=" ml-12"
            block
            color="success"
            type="submit"
            :disabled="isUploading"
            >{{ $t("createArticle.general.Create article") }}</v-btn
          >
        </v-col>
      </v-row>
      <v-col></v-col>
    </v-row>
  </v-form>
</template>

<script>
// import Vue from "vue";

import { mapActions, mapGetters } from "vuex";
import messages from "./modules/lang/main/export";
import CreateArticleInfoDialog from "@/components/informational_dialogs/private/createArticle_info_dialog.vue";
import UpdateArticleInfoDialog from "@/components/informational_dialogs/private/updateArticle_info_dialog.vue";
import "@/assets/flag-icon.css";

// Vue.use(Croppa);
export default {
  i18n: { messages },
  components: {
    CreateArticleInfoDialog,
    UpdateArticleInfoDialog,
  },
  data() {
    return {
      rules: {
        max250Chars: (value) => value.length <= 250 || "Max. 250 characters",
      },
      createArticleInfoDialog: false,
      updateArticleInfoDialog: false,
      languagePicked: "",
      iconsize: "2em",
      imageCropper: {},
      imgRotating: false,
      SERVERADRESS: process.env.VUE_APP_SERVERADDRESS,
      isUploading: false,
      Id: null,
      ArticleData: {},
      Published: false,
      Comments: false,
      Category: null,
      CurrentPublished: false,
      Title: "",
      Content: "",
      GeoTags: [],
      Geo: {
        SearchTags: [],
        SearchTimeout: null,
        IsLoading: false,
        Search: "",
      },
      Tags: [],
      Tag: {
        SearchTags: [],
        SearchTimeout: null,
        IsLoading: false,
        Search: "",
      },
      Lang:
        localStorage.getItem("setlanguage") ||
        localStorage.getItem("detectedlanguage") ||
        "en",
      filedata: null,
      MainImage: {
        ExifRotation: 1,
        Exists: false,
        Id: 0,
        Dirty: false,
        Name: "",
        Url: "",
        File: "",
      },
    };
  },
  watch: {
    "Geo.Search"(newVal) {
      // if (this.Geo.IsLoading) return;
      this.Geo.IsLoading = true;
      clearTimeout(this.Geo.SearchTimeout);
      this.Geo.SearchTimeout = setTimeout(() => {
        this.SEARCH_GEO_SUGGESTION(newVal)
          .then((resp) => {
            this.Geo.SearchTags = resp.data.concat(this.GeoTags);
          })
          .catch((ex) => console.log(ex))
          .finally(() => {
            this.Geo.IsLoading = false;
          });
      }, 200);
    },
    Title(newVal) {
      if (!this.ArticleData.hasOwnProperty(this.Lang)) {
        this.ArticleData[this.Lang] = { new: true };
      }
      let _CurrentData = this.ArticleData[this.Lang];

      _CurrentData.Title = newVal;
      _CurrentData.Content = this.Content;
    },
    Content(newVal) {
      if (!this.ArticleData.hasOwnProperty(this.Lang)) {
        this.ArticleData[this.Lang] = { new: true };
      }
      let _CurrentData = this.ArticleData[this.Lang];
      _CurrentData.Content = newVal;
      _CurrentData.Title = this.Title;
    },
    Lang(newVal, oldVal) {
      let _OldData = this.ArticleData[oldVal];
      if (_OldData.new) {
        delete this.ArticleData[oldVal];
      }
      if (!this.ArticleData.hasOwnProperty(this.Lang)) {
        this.ArticleData[this.Lang] = { new: true };
      } else {
        let _CurrentData = this.ArticleData[newVal];
        this.Title = _CurrentData.Title;
        this.Content = _CurrentData.Content;
      }
    },
    $route: "fetchData",
    Published(newVal) {
      if (newVal) {
        if (this.GeoTags.length == 0 || this.Tags.length == 0) {
          this.$emit(
            "snackbarevent",
            this.$i18n.t(
              "createArticle.general.You need to fill inn both GeoTags and Tagging for this to be published"
            )
          );
          setTimeout(() => {
            this.Published = false;
          }, 1000);
        }
      }
    },
  },
  computed: {
    // Takes a look at the name given in the router, and define if its an create article or update.
    isArticleUpdate() {
      return this.$route.name === "createarticle";
    },

    PublishText() {
      if (this.CurrentPublished && !this.Published && this.Id > 0) {
        return this.$i18n.t(
          "createArticle.settings.Unpublish this article when saving"
        );
      } else if (!this.CurrentPublished && this.Published && this.Id > 0) {
        return this.$i18n.t(
          "createArticle.settings.Publish this article when saving"
        );
      } else if (this.CurrentPublished && this.Published && this.Id > 0) {
        return this.$i18n.t("createArticle.settings.Article is published");
      } else if (!this.CurrentPublished && !this.Published && this.Id > 0) {
        return this.$i18n.t("createArticle.settings.Article is NOT published");
      } else if (this.Published) {
        // do nothing
        return this.$i18n.t(
          "createArticle.settings.Article will be published on save"
        );
      } else {
        return this.$i18n.t(
          "createArticle.settings.Article will NOT be published on save"
        );
      }
    },

    ...mapGetters(["getLanguages", "getTags"]),
    MainTags() {
      return this.getTags.filter((tag) => tag.Depth == 2);
    },
    TagsOrdered() {
      // let _Tags = this.getLanguages.filter(tag => tag.Depth > 1);
      let _Tags = this.getTags.filter((tag) => tag.Depth > 1);
      return _Tags
        .map((tag) => {
          return {
            ...tag,
            Label: tag.TranslatedLabel ? tag.TranslatedLabel : tag.Label,
          };
        })
        .sort((a, b) => {
          if (a.Lft < b.lft) return -1;
          if (a.Lft > b.lft) return 1;
          return 0;
        });
    },
  },
  methods: {
    ...mapActions([
      "SUBMIT_ARTICLE",
      "DELETE_ARTICLE",
      "GET_ARTICLE",
      "SEARCH_GEO_SUGGESTION",
      "SEARCH_TAG_SUGGESTION",
    ]),

    // validate that title isnt longer than 250 characters
    validateArticleTitle(value) {
      if (value.length > 10) {
        this.msg["title"] = "Title are too long";
      } else {
        this.msg["title"] = "";
      }
    },

    pasteEventRemoveHtml(e) {
      var text = (event.clipboardData || window.clipboardData).getData("text");
      let paste = true;
      if (document.queryCommandSupported("insertText")) {
        if (!document.execCommand("insertText", false, text)) {
          paste = false;
        }
      } else {
        if (!document.execCommand("paste", false, text)) {
          paste = false;
        }
      }
      if (paste) {
        console.log("paste is true");
        e.preventDefault();
      }
    },
    removeGeoTag(item) {
      this.GeoTags = this.GeoTags.filter((geo) => geo.id != item.id);
    },
    removeTag(item) {
      this.Tags = this.Tags.filter((geo) => geo.id != item.id);
    },
    selectedGeoEvent(evt) {
      this.Geo.Search = "";
    },
    selectedTagEvent(evt) {
      this.Tag.Search = "";
    },
    fetchData() {
      // if (this.$route.params.articleid) {

      // } else {
      //   this.Id = null;
      //   this.Title = "";
      //   this.Content = "";
      //   this.ArticleData = {};
      //   this.MainImage.Exists = false;
      //   this.MainImage.Id = 0;
      //   this.MainImage.Dirty = false;
      //   this.MainImage.Name = "";
      //   this.MainImage.Url = "";
      //   this.Tags = [];
      //   this.GeoTags = [];
      //   this.MainImage.File = { name: "", size: "", type: "" };
      // }

      this.GET_ARTICLE(this.$route.params.articleid)
        .then((res) => {
          console.log(res);
          this.Lang =
            res.data.OrgLanguage ||
            localStorage.getItem("setlanguage") ||
            localStorage.getItem("detectedlanguage") ||
            "en";
          this.Id = res.data.id || null;
          this.CurrentPublished = res.data.Published || false;
          this.Published = res.data.Published || false;
          this.Comments = res.data.Comments || false;
          this.Category =
            this.MainTags.find((mt) => mt.id == res.data.Category) || null;
          this.ArticleData = {};
          this.GeoTags = res.data.GeoTags || [];
          this.Geo.SearchTags = res.data.GeoTags;
          // this.Tag.SearchTags = res.data.SearchTags;
          this.Tags = res.data.Tags || [];
          if (res.data.hasOwnProperty("Content")) {
            res.data.Content.forEach((element) => {
              this.ArticleData[element.Lang] = {
                Title: element.Title,
                Content: element.Content,
              };
            });
          }
          if (this.ArticleData && this.ArticleData.hasOwnProperty(this.Lang)) {
            this.Title = this.ArticleData[this.Lang]["Title"];
            this.Content = this.ArticleData[this.Lang]["Content"];
          } else {
            this.ArticleData[this.Lang] = {
              new: true,
              Title: "",
              Content: "",
            };
            this.Title = "";
            this.Content = "";
          }
          if (res.data.Media && res.data.Media.length == 1) {
            let _MediaData = res.data.Media.pop();
            this.MainImage.Exists = true;
            this.MainImage.Id = _MediaData.id;
            this.MainImage.Name = _MediaData.FileOriginalName;
            this.MainImage.Url = `${process.env.VUE_APP_BASEAPI}/file/${_MediaData.MediaURL}`;
            this.MainImage.File = {
              size: _MediaData.FileSize,
              type: _MediaData.FileType,
              name: _MediaData.FileOriginalName,
            };
            this.MainImage.Width = _MediaData.ImageWidth;
            this.MainImage.Height = _MediaData.ImageHeight;
          } else {
            this.MainImage.Dirty = false;
            this.MainImage.Exists = false;
            this.MainImage.File = "";
            this.MainImage.Name = "";
            this.MainImage.Url = "";
          }
        })
        .catch((ex) => {
          console.error(ex);
        });
    },
    async submitArticle() {
      let _Article = {
        id: this.Id,
        Title: this.Title,
        Content: this.Content,
        Lang: this.Lang,
        Data: this.ArticleData,
        GeoTags: this.GeoTags,
        Tags: this.Tags,
        Published: this.Published,
        Comments: this.Comments,
        Category: this.Category && this.Category.id,
      };
      _Article.Content = _Article.Content.replace(/</g, "&lt;").replace(
        />/g,
        "&gt;"
      );

      if (this.MainImage.Dirty) {
        if (this.filedata && this.MainImage.Dirty) {
          _Article.Media = [await this.uploadFile()];
        } else {
          _Article.Media = [];
        }
      } else {
        _Article.Media = null;
      }
      this.isUploading = true;
      if (_Article.GeoTags.length == 0 || _Article.Tags.length == 0) {
        _Article.Published = false;
      }

      this.SUBMIT_ARTICLE(_Article)
        .then((resp) => {
          this.MainImage.Dirty = false;
          console.log(resp);
          if (this.$route.name != "editarticle") {
            if (resp.data.id && this.$route.params.articleid != resp.data.id) {
              console.log("what is this");
              this.$router.push({
                name: "editarticle",
                params: { articleid: resp.data.id },
              });
            } else {
              throw "Wrong data in return.";
            }
          } else {
            this.MainImage.Dirty = false;
            this.fetchData();
          }
        })
        .catch((ex) => {
          this.$emit(
            "snackbarevent",
            "An error occured during submit, your changes was not saved.\n" + ex
          );
          console.error(ex);
        })
        .finally(() => {
          this.isUploading = false;
          this.$router.push("/private/articles/list");
        });
    },
    deleteArticle() {
      let _confirm = confirm(
        this.$t(
          "createArticle.general.Are you sure you want to delete this article?"
        )
      );
      if (_confirm) {
        let _Article = {
          id: this.Id,
        };
        this.DELETE_ARTICLE(_Article)
          .then(() => {
            this.$router.push({
              name: "listarticles",
            });
          })
          .catch((ex) => {
            console.error(ex);
            this.$emit(
              "snackbarevent",
              "An error occured during submit, your changes was not saved.\n" +
                ex
            );
          });
      }
    },

    setImgDirty(type) {
      if (type == "add") {
        this.MainImage.Exists = true;
        this.MainImage.Id = null;
      } else {
        this.filedata = null;
        this.MainImage.Id = null;
        this.MainImage.Exists = false;
        this.MainImage.File = "";
        this.MainImage.Name = "";
        this.MainImage.Url = "";
      }
      this.MainImage.Dirty = true;
    },
    async handleFileSelect(event) {
      let files = event.target.files;
      let file = files[0];
      let filedata = await this.handleFileLoad(file);

      this.filedata = filedata;
      this.MainImage.Dirty = true;
      this.MainImage.Exists = true;
      this.MainImage.Id = 0;
      this.MainImage.Name = this.filedata.name;
      this.MainImage.Url = this.filedata.dataurl;
    },
    handleFileLoad(currentFile) {
      return new Promise((resolve, reject) => {
        let filedata = {
          name: currentFile.name,
          size: currentFile.size,
          type: currentFile.type,
          width: 0,
          height: 0,
          dataurl: "",
        };
        let reader = new FileReader();
        reader.onload = () => {
          filedata.dataurl = reader.result;
          let img = new Image();
          img.onload = () => {
            filedata.width = img.width;
            filedata.height = img.height;
            this.$refs.fileupload.value = "";
            resolve(filedata);
          };
          img.src = filedata.dataurl;
        };
        reader.readAsDataURL(currentFile);
      });
    },
    uploadFile() {
      return new Promise((resolve) => {
        resolve(this.filedata);
      });
    },
  },
  mounted() {
    this.fetchData();
  },
};

function ResizeImage(dataurl) {
  function createImage(src) {
    return new Promise((resolve, reject) => {
      var i = new Image();
      i.onload = () => {
        resolve(i);
      };
      i.src = src;
    });
  }
  function resize(image) {
    return new Promise((resolve, reject) => {
      let canvas = document.createElement("canvas");
      let sizes = calculateSizes(image);
      canvas.width = sizes.newWidth;
      canvas.height = sizes.newHeight;
      let ctx = canvas.getContext("2d");
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      // while (canvas.width > 1000 || canvas.height > 1000) {
      //   canvas = halfSize(canvas);
      // }

      resolve(canvas.toDataURL("image/jpeg"));
    });
  }

  function halfSize(i) {
    var canvas = document.createElement("canvas");
    canvas.width = i.width / 2;
    canvas.height = i.height / 2;
    var ctx = canvas.getContext("2d");
    ctx.drawImage(i, 0, 0, canvas.width, canvas.height);
    return canvas;
  }
  function calculateSizes(img) {
    let originalHeight = img.height;
    let originalWidth = img.width;
    let ratioY = 1000 / originalHeight;
    let ratioX = 1000 / originalWidth;
    // use whichever multiplier is smaller
    let ratio = ratioX < ratioY ? ratioX : ratioY;
    let newHeight = Math.round(originalHeight * ratio);
    let newWidth = Math.round(originalWidth * ratio);
    console.log({
      originalHeight,
      originalWidth,
      ratioY,
      ratioX,
      ratio,
      newHeight,
      newWidth,
    });
    return {
      newHeight,
      newWidth,
    };
  }
  return new Promise((resolve, reject) => {
    createImage(dataurl)
      .then((img) => resize(img))
      .then((resp) => {
        console.log("done");
        resolve(resp);
      });
  });
}

function getOrientation(file, callback) {
  var reader = new FileReader();
  reader.onload = function(e) {
    var view = new DataView(e.target.result);
    if (view.getUint16(0, false) != 0xffd8) {
      return callback(-2);
    }
    var length = view.byteLength,
      offset = 2;
    while (offset < length) {
      if (view.getUint16(offset + 2, false) <= 8) return callback(-1);
      var marker = view.getUint16(offset, false);
      offset += 2;
      if (marker == 0xffe1) {
        if (view.getUint32((offset += 2), false) != 0x45786966) {
          return callback(-1);
        }

        var little = view.getUint16((offset += 6), false) == 0x4949;
        offset += view.getUint32(offset + 4, little);
        var tags = view.getUint16(offset, little);
        offset += 2;
        for (var i = 0; i < tags; i++) {
          if (view.getUint16(offset + i * 12, little) == 0x0112) {
            return callback(view.getUint16(offset + i * 12 + 8, little));
          }
        }
      } else if ((marker & 0xff00) != 0xff00) {
        break;
      } else {
        offset += view.getUint16(offset, false);
      }
    }
    return callback(-1);
  };
  reader.readAsArrayBuffer(file);
}
</script>

<style scoped>
.imageuploadwrapper {
  width: 100%;
  height: 400px;
  background: #e6e6e6;
  text-align: center;
  display: flex;
  align-items: center;
  cursor: pointer;
}
.imageuploadwrapper:hover {
  outline: 3px dotted black;
}
.uploadImageContainer {
  margin-right: 10px;
  background-color: #eeeeee;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
